import React, { useEffect, useRef, useState } from "react";
import {
  createUserWithEmailAndPassword,
  deleteUser,
  onAuthStateChanged,
  reload,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signOut,
  verifyBeforeUpdateEmail,
} from "firebase/auth";
import { collection, deleteDoc, doc, getDoc, getDocs, serverTimestamp, setDoc, updateDoc } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import "./App.css";
import { SectionCard } from "./budgetComponents";
import BudgetDashboard from "./BudgetDashboard";
import FlowLogo from "./FlowLogo";
import { auth, db, functions } from "./firebase";

const FLOW_PLAN_OPTIONS = [
  { id: "lite", label: "FLOW Lite" },
  { id: "system", label: "FLOW System" },
  { id: "control", label: "FLOW Control" },
];

const FLOW_PLAN_IDS = FLOW_PLAN_OPTIONS.map((plan) => plan.id);

function normalizeEmail(value = "") {
  return value.trim().toLowerCase();
}

function normalizeAccessCode(value = "") {
  return value.trim();
}

function generateInviteCode(plan = "lite") {
  const planPrefix = plan === "system" ? "SYSTEM" : plan === "control" ? "CONTROL" : "LITE";
  const randomValues = new Uint32Array(2);
  window.crypto.getRandomValues(randomValues);
  const randomPart = Array.from(randomValues)
    .map((value) => value.toString(36).toUpperCase().padStart(7, "0"))
    .join("")
    .slice(0, 10);
  return `FLOW-${planPrefix}-${randomPart}`;
}

function normalizeAccess(access = {}) {
  const plan = FLOW_PLAN_IDS.includes(access.plan) ? access.plan : "";
  return {
    plan,
    planStatus: plan ? access.planStatus || "active" : "pending",
    source: access.source || "",
    inviteCode: access.inviteCode || "",
  };
}

function isAccessActive(access = {}) {
  return FLOW_PLAN_IDS.includes(access.plan) && access.planStatus === "active";
}

function getDashboardModeForAccess(access = {}) {
  return access.plan === "lite" ? "lite" : "full";
}

function getPlanLabel(plan = "") {
  return FLOW_PLAN_OPTIONS.find((option) => option.id === plan)?.label || "No active plan";
}

function getStatusLabel(status = "") {
  const normalized = String(status || "pending").toLowerCase();

  if (normalized === "active") return "Active";
  if (normalized === "suspended") return "Suspended";
  if (normalized === "used") return "Used";
  if (normalized === "pending") return "Pending";

  return normalized ? normalized.charAt(0).toUpperCase() + normalized.slice(1) : "Pending";
}

function getStatusPillClass(status = "") {
  const normalized = String(status || "pending").toLowerCase();

  if (normalized === "active") return "border-[#2A5B44] bg-[#0E2119] text-[#CFFBE4]";
  if (normalized === "suspended") return "border-[#7A1E1E] bg-[#3A0909] text-[#FFB4B4]";
  if (normalized === "used") return "border-[#4A3A6A] bg-[#160E24] text-[#C9A6FF]";

  return "border-[#5A4A1D] bg-[#211B0E] text-[#FFE7A3]";
}

function formatAdminTimestamp(value) {
  if (!value) return "-";
  if (typeof value.toDate === "function") return value.toDate().toLocaleString();
  if (value.seconds) return new Date(value.seconds * 1000).toLocaleString();
  return "-";
}

function getPlanAccessGrantedMessage(plan = "") {
  return `Congratulations. ${getPlanLabel(plan)} access granted.`;
}

function getTodayInputValue() {
  return new Date().toISOString().slice(0, 10);
}

function createInitialMixedIncomeState() {
  return {
    w2InputMode: "gross",
    w2PayStructure: "fixed",
    w2Frequency: "Bi-weekly",
    w2BaseIncome: "",
    w2WithholdingPercent: "",
    w2WithholdingAmount: "",
    w2HourlyRate: "",
    w2TypicalHours: "",
    w2ActualHours: "",
    w2OvertimeHours: "",
    w2OvertimeMultiplier: "1.5",
    w2Payday: "Friday",
    w2LastPaycheckDate: "",
    contractorFrequency: "Monthly",
    contractorBaseIncome: "",
    contractorTaxPercent: "",
    contractorPayday: "Friday",
    contractorLastPaycheckDate: "",
    contractorWriteOffMode: "medium",
    contractorCustomWriteOffPercent: 35,
    contractorCustomPayDates: [{ id: 1, date: "", amount: "" }],
  };
}

function createInitialExpensePlan() {
  return [
    { id: 1, name: "Mortgage / Rent", amount: "", planType: "fixed", categoryKey: "housing" },
    { id: 2, name: "Utilities", amount: "", planType: "fixed", categoryKey: "utilities" },
    { id: 3, name: "Insurance", amount: "", planType: "fixed", categoryKey: "insurance" },
    { id: 4, name: "Phone / Internet", amount: "", planType: "fixed", categoryKey: "phone-internet" },
    { id: 5, name: "Food", amount: "", planType: "flexible", categoryKey: "food" },
    { id: 6, name: "Transportation", amount: "", planType: "flexible", categoryKey: "transportation" },
  ];
}

function inferExpensePlanType(name = "") {
  const normalized = name.trim().toLowerCase();
  if (!normalized) return "flexible";

  const fixedKeywords = [
    "mortgage",
    "rent",
    "car payment",
    "loan",
    "insurance",
    "phone",
    "internet",
    "utilities",
    "electric",
    "water",
    "gas bill",
    "child care",
    "daycare",
    "subscription",
  ];

  return fixedKeywords.some((keyword) => normalized.includes(keyword)) ? "fixed" : "flexible";
}

function normalizeBudgetCategoryKey(value = "") {
  const normalized = value.trim().toLowerCase();
  if (!normalized) return "other";

  if (normalized.includes("mortgage") || normalized.includes("rent") || normalized.includes("housing")) return "housing";
  if (normalized.includes("utility") || normalized.includes("electric") || normalized.includes("water") || normalized.includes("power")) return "utilities";
  if (normalized.includes("insurance")) return "insurance";
  if (normalized.includes("phone") || normalized.includes("internet")) return "phone-internet";
  if (normalized.includes("food") || normalized.includes("grocery") || normalized.includes("meal") || normalized.includes("restaurant")) return "food";
  if (normalized.includes("transport") || normalized.includes("gas") || normalized.includes("fuel")) return "transportation";
  if (normalized.includes("subscription") || normalized.includes("stream")) return "subscriptions";
  if (normalized.includes("entertainment")) return "entertainment";
  if (normalized.includes("health") || normalized.includes("medical")) return "health";
  if (normalized.includes("debt")) return "debt-payment";
  if (normalized.includes("tax")) return "tax-payment";
  if (normalized.includes("income") || normalized.includes("paycheck")) return "income";
  if (normalized.includes("saving") || normalized.includes("transfer")) return "savings-transfer";
  return normalized;
}

function normalizeExpenses(expenses = []) {
  return expenses.map((row, index) => {
    const { type, ...rest } = row || {};
    return {
      id: rest.id ?? index + 1,
      name: rest.name ?? "",
      amount: rest.amount ?? "",
      planType: rest.planType || inferExpensePlanType(rest.name),
      categoryKey: rest.categoryKey || normalizeBudgetCategoryKey(rest.name),
    };
  });
}

function createInitialBudgetState() {
  return {
    activeTab: "control",
    incomeType: "w2",
    incomeFrequency: "Weekly",
    baseIncome: "",
    taxPercent: "",
    writeOffMode: "medium",
    taxProfile: {
      entityType: "w2-only",
      paymentStyle: "withholding",
      w2ResidenceState: "FL",
      w2FilingStatus: "single",
      w2QualifyingChildren: "",
      w2OtherDependents: "",
      w2ExtraFederalWithholding: "",
      w2StateWithholdingAmount: "",
    },
    customWriteOffPercent: 35,
    customPayDates: [{ id: 1, date: "", amount: "" }],
    primaryPayday: "Friday",
    primaryLastPaycheckDate: "",
    incomePreviewMonth: getTodayInputValue().slice(0, 7),
    mixedIncome: createInitialMixedIncomeState(),
    deductions: [
      "Medicaid",
      "Social Security",
      "Retirement",
      "Health Insurance",
      "Child Support",
      "Other",
    ].map((name) => ({ name, mode: "%", value: "" })),
    additionalIncomes: [
      { id: 1, source: "", frequency: "Monthly", amount: "" },
      { id: 2, source: "", frequency: "Monthly", amount: "" },
    ],
    expenses: createInitialExpensePlan(),
    transactions: [],
    transactionForm: {
      date: getTodayInputValue(),
      amount: "",
      type: "expense",
      category: "",
      account: "Checking",
      description: "",
    },
    transactionMonth: getTodayInputValue().slice(0, 7),
    debts: [
      { id: 1, name: "Credit Card", type: "Credit Card", amount: "", balance: "", interest: "" },
      { id: 2, name: "Auto Loan", type: "Loan", amount: "", balance: "", interest: "" },
    ],
    customGoals: [],
    goalForm: {
      name: "",
      type: "savings",
      targetAmount: "",
      linkedDebtId: "",
      contributionStyle: "monthly",
      contributionMode: "recommended",
      recommendationLevel: "balanced",
      customContribution: "",
    },
    timelineFocus: "",
    debtStrategyTarget: "all",
    debtStrategyMethod: "snowball",
    debtStrategyExtraAmount: "",
  };
}

function createLiteBudgetState() {
  return mergeBudgetState({
    activeTab: "overview",
    liteOnboardingComplete: false,
    incomeType: "w2",
    incomeFrequency: "Monthly",
    baseIncome: "",
    additionalIncomes: [],
    expenses: [
      { id: 1, name: "Mortgage / Rent", amount: "", planType: "fixed", categoryKey: "housing" },
      { id: 2, name: "Utilities", amount: "", planType: "fixed", categoryKey: "utilities" },
      { id: 3, name: "Insurance", amount: "", planType: "fixed", categoryKey: "insurance" },
      { id: 4, name: "Phone / Internet", amount: "", planType: "fixed", categoryKey: "phone-internet" },
      { id: 5, name: "Food", amount: "", planType: "flexible", categoryKey: "food" },
      { id: 6, name: "Gas / Transportation", amount: "", planType: "flexible", categoryKey: "transportation" },
      { id: 7, name: "Subscriptions", amount: "", planType: "flexible", categoryKey: "subscriptions" },
      { id: 8, name: "Debt Payments", amount: "", planType: "fixed", categoryKey: "debt-payment" },
      { id: 9, name: "Health / Medical", amount: "", planType: "flexible", categoryKey: "health" },
      { id: 10, name: "Miscellaneous", amount: "", planType: "flexible", categoryKey: "other" },
    ],
    transactions: [],
    debts: [],
    customGoals: [],
  });
}

function createDemoBudgetState(mode = "full") {
  if (mode === "lite") {
    return createLiteBudgetState();
  }

  const demoMonth = getTodayInputValue().slice(0, 7);

  return mergeBudgetState({
    activeTab: "control",
    incomeType: "w2",
    incomeFrequency: "Bi-weekly",
    baseIncome: "2200",
    mixedIncome: {
      ...createInitialMixedIncomeState(),
      w2InputMode: "gross",
      w2PayStructure: "fixed",
      w2Frequency: "Bi-weekly",
      w2BaseIncome: "2200",
      w2Payday: "Friday",
    },
    additionalIncomes: [
      { id: 1, source: "Child Support", frequency: "Monthly", amount: "450" },
      { id: 2, source: "Weekend Side Gig", frequency: "Monthly", amount: "300" },
    ],
    expenses: [
      { id: 1, name: "Rent", amount: "1800", planType: "fixed", categoryKey: "housing" },
      { id: 2, name: "Utilities", amount: "260", planType: "fixed", categoryKey: "utilities" },
      { id: 3, name: "Insurance", amount: "180", planType: "fixed", categoryKey: "insurance" },
      { id: 4, name: "Phone / Internet", amount: "140", planType: "fixed", categoryKey: "phone-internet" },
      { id: 5, name: "Food", amount: "700", planType: "flexible", categoryKey: "food" },
      { id: 6, name: "Transportation", amount: "380", planType: "flexible", categoryKey: "transportation" },
      { id: 7, name: "Subscriptions", amount: "55", planType: "flexible", categoryKey: "subscriptions" },
      { id: 8, name: "Entertainment", amount: "125", planType: "flexible", categoryKey: "entertainment" },
    ],
    transactions: [
      { id: 1, date: `${demoMonth}-03`, amount: "1125", type: "income", category: "Paycheck", account: "Checking", description: "Bi-weekly paycheck" },
      { id: 2, date: `${demoMonth}-05`, amount: "1800", type: "expense", category: "Housing", account: "Checking", description: "Rent" },
      { id: 3, date: `${demoMonth}-07`, amount: "145", type: "expense", category: "Utilities", account: "Checking", description: "Water + electric" },
      { id: 4, date: `${demoMonth}-09`, amount: "86", type: "expense", category: "Food", account: "Debit Card", description: "Groceries" },
      { id: 5, date: `${demoMonth}-12`, amount: "1125", type: "income", category: "Paycheck", account: "Checking", description: "Bi-weekly paycheck" },
      { id: 6, date: `${demoMonth}-14`, amount: "62", type: "expense", category: "Transportation", account: "Credit Card", description: "Gas" },
      { id: 7, date: `${demoMonth}-16`, amount: "450", type: "income", category: "Child Support", account: "Checking", description: "Monthly support" },
      { id: 8, date: `${demoMonth}-18`, amount: "41", type: "expense", category: "Subscriptions", account: "Checking", description: "Streaming services" },
    ],
    transactionMonth: demoMonth,
    debts: [
      { id: 1, name: "Credit Card", type: "Credit Card", amount: "175", balance: "4200", interest: "24.9" },
      { id: 2, name: "Auto Loan", type: "Loan", amount: "350", balance: "9800", interest: "6.4" },
    ],
    customGoals: [
      {
        id: 1,
        name: "Starter Emergency Fund",
        type: "savings",
        targetAmount: "1000",
        linkedDebtId: "",
        contributionStyle: "monthly",
        contributionMode: "recommended",
        recommendationLevel: "balanced",
        customContribution: "150",
      },
    ],
  });
}

function isDemoQueryEnabled() {
  if (typeof window === "undefined") {
    return false;
  }

  return new URLSearchParams(window.location.search).get("demo") === "1";
}

function getProductModeFromQuery() {
  if (typeof window === "undefined") {
    return "full";
  }

  return new URLSearchParams(window.location.search).get("mode") === "lite" ? "lite" : "full";
}

function setDemoQueryEnabled(enabled) {
  if (typeof window === "undefined") {
    return;
  }

  const url = new URL(window.location.href);
  if (enabled) {
    url.searchParams.set("demo", "1");
  } else {
    url.searchParams.delete("demo");
  }
  window.history.replaceState({}, "", url.toString());
}

function setProductModeQuery(mode) {
  if (typeof window === "undefined") {
    return;
  }

  const url = new URL(window.location.href);
  if (mode === "lite") {
    url.searchParams.set("mode", "lite");
  } else {
    url.searchParams.delete("mode");
  }
  window.history.replaceState({}, "", url.toString());
}

function mergeBudgetState(savedState = {}) {
  const defaults = createInitialBudgetState();

  return {
    ...defaults,
    ...savedState,
    taxProfile: {
      ...defaults.taxProfile,
      ...(savedState.taxProfile || {}),
    },
    mixedIncome: {
      ...defaults.mixedIncome,
      ...(savedState.mixedIncome || {}),
      contractorCustomPayDates:
        savedState?.mixedIncome?.contractorCustomPayDates?.length > 0
          ? savedState.mixedIncome.contractorCustomPayDates
          : defaults.mixedIncome.contractorCustomPayDates,
    },
    goalForm: {
      ...defaults.goalForm,
      ...(savedState.goalForm || {}),
    },
    expenses: normalizeExpenses(savedState.expenses || defaults.expenses),
  };
}

function createInitialAuthForm() {
  return {
    fullName: "",
    preferredName: "",
    dateOfBirth: "",
    inviteCode: "",
    email: "",
    password: "",
    confirmPassword: "",
  };
}

function normalizeUserProfile(profile = {}) {
  return {
    fullName: profile.fullName ?? "",
    preferredName: profile.preferredName ?? "",
    dateOfBirth: profile.dateOfBirth ?? "",
    preferences: {
      themeMode: profile?.preferences?.themeMode ?? "theme-dark-plum",
      accentColor: profile?.preferences?.accentColor ?? "plum",
    },
  };
}

async function redeemAccessCode({ code, user, profile, budgetState, createUserRecord = false }) {
  const cleanCode = normalizeAccessCode(code);

  if (!cleanCode) {
    throw new Error("missing-code");
  }

  if (!user?.uid) {
    throw new Error("missing-user");
  }

  const redeemAccessCodeCallable = httpsCallable(functions, "redeemAccessCode");
  const response = await redeemAccessCodeCallable({
    code: cleanCode,
    profile,
    budgetState,
    createUserRecord,
  });

  return normalizeAccess(response.data?.access || {});
}

function getInviteCodeErrorMessage(error) {
  const message = error?.message || "";
  const code = error?.code || "";
  if (message.includes("missing-code")) return "Enter your FLOW invite code.";
  if (message.includes("invalid-code")) return "That invite code was not found. Check the code and try again.";
  if (message.includes("used-code")) return "That invite code has already been used.";
  if (message.includes("expired-code")) return "That invite code has expired.";
  if (message.includes("inactive-code")) return "That invite code is not active.";
  if (message.includes("invalid-plan")) return "That invite code is not connected to a valid FLOW plan.";
  if (message.includes("gumroad-not-configured") || code.includes("failed-precondition")) return "Gumroad license redemption is not configured yet. Use a FLOW invite code or contact support.";
  if (message.includes("gumroad-invalid-license") || code.includes("not-found")) return "That Gumroad license key was not found. Check the key from your Gumroad purchase and try again.";
  if (message.includes("gumroad-buyer-email-mismatch") || code.includes("permission-denied")) return "That Gumroad license belongs to a different buyer email. Sign in with the purchase email or contact support.";
  if (message.includes("too-many-license-attempts") || code.includes("resource-exhausted")) return "Too many license attempts. Wait a few minutes and try again.";
  if (message.includes("app-check") || code.includes("failed-precondition")) return "FLOW could not verify this browser session. Refresh the page and try again.";
  if (message.includes("gumroad-refunded") || message.includes("gumroad-chargeback")) return "That Gumroad purchase is no longer eligible for access.";
  if (message.includes("gumroad-license-used") || code.includes("already-exists")) return "That Gumroad license key has already been used.";
  if (message.includes("gumroad-plan-mismatch") || code.includes("invalid-argument")) return "That code is not connected to a valid FLOW plan.";
  if (message.includes("auth-required") || code.includes("unauthenticated")) return "FLOW could not verify your signed-in session. Refresh the page and try again.";
  if (message.includes("gumroad-unavailable") || code.includes("unavailable")) return "Gumroad license verification is temporarily unavailable. Try again in a minute.";
  if (code.includes("internal")) return "FLOW could not reach the license server. Try again in a minute or contact support.";
  return "We could not redeem that access code. Please try again.";
}

function LoadingScreen({ label }) {
  return (
    <div className="min-h-screen w-full bg-[#0E0A12] text-[#D8D8D8] px-4 py-6 sm:px-6 lg:px-8 flex items-center justify-center">
      <div className="w-full max-w-md rounded-3xl border border-[#2A0047] bg-black/80 p-8 shadow-xl text-center">
        <FlowLogo centered showTagline={false} className="justify-center" />
        <p className="mt-3 text-sm text-[#D8D8D8]">{label}</p>
      </div>
    </div>
  );
}

function AuthScreen({
  authMode,
  authForm,
  authError,
  authNotice,
  authBusy,
  onChangeMode,
  onChangeField,
  onSubmit,
  onForgotPassword,
  onTryDemo,
}) {
  return (
    <div className="min-h-screen w-full bg-[#0E0A12] text-[#D8D8D8] px-4 py-6 sm:px-6 lg:px-8 flex items-center justify-center">
      <div className="w-full max-w-5xl flex flex-col gap-6">
        <div className="flex justify-center">
          <FlowLogo centered />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_0.8fr] gap-6">
          <SectionCard
            title="FLOW"
            subtitle="A budgeting home that remembers each user, follows them across devices, and helps you get control of real-life money decisions."
            dark
          >
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
              <div className="rounded-2xl border border-[#2A0047] bg-[#130018] p-4">
                <p className="text-[#C9A6FF] font-semibold">Your account</p>
                <p className="mt-2 text-xs text-[#D8D8D8]">Sign in from your phone or computer and your budget comes back with you.</p>
              </div>
              <div className="rounded-2xl border border-[#2A0047] bg-[#130018] p-4">
                <p className="text-[#C9A6FF] font-semibold">Your numbers</p>
                <p className="mt-2 text-xs text-[#D8D8D8]">Income, expenses, debts, goals, and planning settings are saved to your account.</p>
              </div>
              <div className="rounded-2xl border border-[#2A0047] bg-[#130018] p-4">
                <p className="text-[#C9A6FF] font-semibold">Your style</p>
                <p className="mt-2 text-xs text-[#D8D8D8]">Once inside, you can update your profile, reset your password, verify your email, and choose a theme that fits you.</p>
              </div>
            </div>
          </SectionCard>

          <SectionCard
            title={authMode === "login" ? "Welcome back" : "Create your account"}
            subtitle={
              authMode === "login"
                ? "Log in to load your budget workspace and pick up where you left off."
                : "Create an account so your budget stays with you across devices. You can start with the basics and fill in the rest later."
            }
          >
            <form className="flex flex-col gap-4" onSubmit={onSubmit}>
              {authMode === "signup" ? (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="flex flex-col gap-2">
                      <span className="text-[#9E4CFF] uppercase tracking-widest text-xs">Full Name</span>
                      <input
                        type="text"
                        value={authForm.fullName}
                        onChange={(event) => onChangeField("fullName", event.target.value)}
                        className="bg-black/70 border border-[#2A0047] rounded-lg px-3 py-2 outline-none focus:border-[#9E4CFF]"
                        placeholder="Your full name"
                        autoComplete="name"
                        required
                      />
                    </div>

                    <div className="flex flex-col gap-2">
                      <span className="text-[#9E4CFF] uppercase tracking-widest text-xs">Preferred Name (Optional)</span>
                      <input
                        type="text"
                        value={authForm.preferredName}
                        onChange={(event) => onChangeField("preferredName", event.target.value)}
                        className="bg-black/70 border border-[#2A0047] rounded-lg px-3 py-2 outline-none focus:border-[#9E4CFF]"
                        placeholder="What should we call you?"
                        autoComplete="nickname"
                      />
                    </div>
                  </div>

                  <div className="flex flex-col gap-2">
                    <span className="text-[#9E4CFF] uppercase tracking-widest text-xs">Date of Birth (Optional)</span>
                    <input
                      type="date"
                      value={authForm.dateOfBirth}
                      onChange={(event) => onChangeField("dateOfBirth", event.target.value)}
                      className="bg-black/70 border border-[#2A0047] rounded-lg px-3 py-2 outline-none focus:border-[#9E4CFF]"
                      autoComplete="bday"
                    />
                    <p className="text-[11px] text-[#D8D8D8]">Only used for your profile. You can skip it for now.</p>
                  </div>

                  <div className="flex flex-col gap-2">
                    <span className="text-[#9E4CFF] uppercase tracking-widest text-xs">FLOW invite code</span>
                    <input
                      type="text"
                      value={authForm.inviteCode}
                      onChange={(event) => onChangeField("inviteCode", event.target.value)}
                      className="bg-black/70 border border-[#2A0047] rounded-lg px-3 py-2 outline-none focus:border-[#9E4CFF]"
                      placeholder="FLOW-XXXX-XXXX or Gumroad license key"
                      autoComplete="one-time-code"
                    />
                    <p className="text-[11px] text-[#D8D8D8]">Enter the access code from your purchase. FLOW invite codes and Gumroad license keys both work here.</p>
                  </div>
                </>
              ) : null}

              <div className="flex flex-col gap-2">
                <span className="text-[#9E4CFF] uppercase tracking-widest text-xs">Email</span>
                <input
                  type="email"
                  value={authForm.email}
                  onChange={(event) => onChangeField("email", event.target.value)}
                  className="bg-black/70 border border-[#2A0047] rounded-lg px-3 py-2 outline-none focus:border-[#9E4CFF]"
                  placeholder="you@example.com"
                  autoComplete="email"
                  required
                />
              </div>

              <div className="flex flex-col gap-2">
                <span className="text-[#9E4CFF] uppercase tracking-widest text-xs">Password</span>
                <input
                  type="password"
                  value={authForm.password}
                  onChange={(event) => onChangeField("password", event.target.value)}
                  className="bg-black/70 border border-[#2A0047] rounded-lg px-3 py-2 outline-none focus:border-[#9E4CFF]"
                  placeholder="At least 6 characters"
                  autoComplete={authMode === "login" ? "current-password" : "new-password"}
                  required
                />
              </div>

              {authMode === "signup" ? (
                <div className="flex flex-col gap-2">
                  <span className="text-[#9E4CFF] uppercase tracking-widest text-xs">Confirm password</span>
                  <input
                    type="password"
                    value={authForm.confirmPassword}
                    onChange={(event) => onChangeField("confirmPassword", event.target.value)}
                    className="bg-black/70 border border-[#2A0047] rounded-lg px-3 py-2 outline-none focus:border-[#9E4CFF]"
                    placeholder="Type it again"
                    autoComplete="new-password"
                    required
                  />
                </div>
              ) : null}

              {authError ? (
                <div className="rounded-xl border border-[#5A1D55] bg-[#2A0A2E] px-3 py-3 text-sm text-[#FFD4F4]">{authError}</div>
              ) : null}

              {authNotice ? (
                <div className="rounded-xl border border-[#2A5B44] bg-[#0E2119] px-3 py-3 text-sm text-[#CFFBE4]">{authNotice}</div>
              ) : null}

              <button
                type="submit"
                className="px-4 py-3 rounded-xl bg-[#9E4CFF] text-black font-semibold tracking-wide shadow-[0_0_16px_#9E4CFF] disabled:opacity-50"
                disabled={authBusy}
              >
                {authBusy
                  ? authMode === "login"
                    ? "Logging in..."
                    : "Creating account..."
                  : authMode === "login"
                  ? "Log In"
                  : "Create Account"}
              </button>
            </form>

            {authMode === "login" ? (
              <button
                type="button"
                className="self-start text-sm text-[#C9A6FF] font-semibold hover:text-white"
                onClick={onForgotPassword}
              >
                Forgot password?
              </button>
            ) : null}

            <div className="border-t border-[#2A0047] pt-4 text-sm text-[#D8D8D8]">
              {authMode === "login" ? "Need an account? " : "Already have an account? "}
              <button
                type="button"
                className="text-[#C9A6FF] font-semibold hover:text-white"
                onClick={() => onChangeMode(authMode === "login" ? "signup" : "login")}
              >
                {authMode === "login" ? "Create one here" : "Log in here"}
              </button>
            </div>
            <button
              type="button"
              className="self-start text-sm text-[#C9A6FF] font-semibold hover:text-white"
              onClick={onTryDemo}
            >
              Try the live demo first
            </button>
          </SectionCard>
        </div>
      </div>
    </div>
  );
}

function AccessRequiredScreen({
  accountEmail,
  access,
  authError,
  authNotice,
  authBusy,
  inviteCode,
  onInviteCodeChange,
  onRedeemInviteCode,
  onLogout,
  onOpenAdmin,
  isAdmin,
}) {
  return (
    <div className="min-h-screen w-full bg-[#0E0A12] text-[#D8D8D8] px-4 py-6 sm:px-6 lg:px-8 flex items-center justify-center">
      <div className="w-full max-w-2xl rounded-3xl border border-[#2A0047] bg-black/80 p-6 sm:p-8 shadow-xl">
        <FlowLogo centered showTagline={false} className="justify-center" />
        <div className="mt-8 text-center">
          <p className="text-xs uppercase tracking-[0.35em] text-[#9E4CFF]">Access required</p>
          <h1 className="mt-3 text-3xl sm:text-4xl font-bold text-white">Unlock FLOW with your invite code.</h1>
          <p className="mt-3 text-sm text-[#D8D8D8]">
            You are signed in as {accountEmail}. This account currently has {getPlanLabel(access.plan)} with status{" "}
            <span className="text-[#C9A6FF] font-semibold">{access.planStatus || "pending"}</span>.
          </p>
        </div>

        <form className="mt-8 flex flex-col gap-4" onSubmit={onRedeemInviteCode}>
          <div className="flex flex-col gap-2">
            <span className="text-[#9E4CFF] uppercase tracking-widest text-xs">Invite code</span>
            <input
              type="text"
              value={inviteCode}
              onChange={(event) => onInviteCodeChange(event.target.value)}
              className="bg-black/70 border border-[#2A0047] rounded-lg px-3 py-3 outline-none focus:border-[#9E4CFF]"
              placeholder="FLOW-XXXX-XXXX or Gumroad license key"
              autoComplete="one-time-code"
              required
            />
          </div>

          {authError ? <div className="rounded-xl border border-[#5A1D55] bg-[#2A0A2E] px-3 py-3 text-sm text-[#FFD4F4]">{authError}</div> : null}
          {authNotice ? <div className="rounded-xl border border-[#2A5B44] bg-[#0E2119] px-3 py-3 text-sm text-[#CFFBE4]">{authNotice}</div> : null}

          <button
            type="submit"
            className="px-4 py-3 rounded-xl bg-[#9E4CFF] text-black font-semibold tracking-wide shadow-[0_0_16px_#9E4CFF] disabled:opacity-50"
            disabled={authBusy}
          >
            {authBusy ? "Checking code..." : "Unlock FLOW"}
          </button>
        </form>

        <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
          {isAdmin ? (
            <button type="button" onClick={onOpenAdmin} className="rounded-xl border border-[#2A0047] bg-black/60 px-4 py-2 text-sm font-semibold text-[#C9A6FF]">
              Open Admin Console
            </button>
          ) : null}
          <button type="button" onClick={onLogout} className="rounded-xl border border-[#2A0047] bg-black/60 px-4 py-2 text-sm font-semibold text-[#C9A6FF]">
            Log out
          </button>
        </div>
      </div>
    </div>
  );
}

function AdminConsole({ currentUser, onClose, onLogout }) {
  const [inviteCodes, setInviteCodes] = useState([]);
  const [users, setUsers] = useState([]);
  const [adminBusy, setAdminBusy] = useState(false);
  const [adminNotice, setAdminNotice] = useState("");
  const [adminError, setAdminError] = useState("");
  const [selectedPlan, setSelectedPlan] = useState("lite");
  const [codeCount, setCodeCount] = useState("5");
  const [adminSearch, setAdminSearch] = useState("");

  const loadAdminData = async () => {
    setAdminBusy(true);
    setAdminError("");
    try {
      const [codeSnapshots, userSnapshots] = await Promise.all([
        getDocs(collection(db, "inviteCodes")),
        getDocs(collection(db, "users")),
      ]);

      const nextCodes = codeSnapshots.docs
        .map((snapshot) => ({ id: snapshot.id, ...snapshot.data() }))
        .sort((a, b) => String(b.createdAt?.seconds || "").localeCompare(String(a.createdAt?.seconds || "")));
      const nextUsers = userSnapshots.docs
        .map((snapshot) => ({ id: snapshot.id, ...snapshot.data() }))
        .sort((a, b) => normalizeEmail(a.email).localeCompare(normalizeEmail(b.email)));

      setInviteCodes(nextCodes);
      setUsers(nextUsers);
    } catch (error) {
      setAdminError("Admin data could not be loaded. Check Firestore rules if this keeps happening.");
    } finally {
      setAdminBusy(false);
    }
  };

  useEffect(() => {
    loadAdminData();
  }, []);

  const activeUsers = users.filter((user) => normalizeAccess(user.access).planStatus === "active").length;
  const suspendedUsers = users.filter((user) => normalizeAccess(user.access).planStatus === "suspended").length;
  const unusedCodes = inviteCodes.filter((code) => !code.used && (code.status || "active") === "active").length;
  const usedCodes = inviteCodes.filter((code) => code.used).length;
  const normalizedAdminSearch = adminSearch.trim().toLowerCase();
  const filteredInviteCodes = normalizedAdminSearch
    ? inviteCodes.filter((code) => {
        const status = code.used ? "used" : code.status || "active";
        const searchableText = [
          code.id,
          code.code,
          code.plan,
          getPlanLabel(code.plan),
          status,
          getStatusLabel(status),
          code.usedBy,
          code.usedByEmail,
          code.createdBy,
        ].join(" ").toLowerCase();

        return searchableText.includes(normalizedAdminSearch);
      })
    : inviteCodes;
  const filteredUsers = normalizedAdminSearch
    ? users.filter((user) => {
        const userAccess = normalizeAccess(user.access);
        const searchableText = [
          user.id,
          user.email,
          user.profile?.fullName,
          user.profile?.preferredName,
          userAccess.plan,
          getPlanLabel(userAccess.plan),
          userAccess.planStatus,
          getStatusLabel(userAccess.planStatus),
          userAccess.source,
          userAccess.inviteCode,
        ].join(" ").toLowerCase();

        return searchableText.includes(normalizedAdminSearch);
      })
    : users;

  const createCodes = async () => {
    const quantity = Math.max(1, Math.min(100, Number(codeCount) || 1));
    setAdminBusy(true);
    setAdminError("");
    setAdminNotice("");

    try {
      const createdCodes = [];
      for (let index = 0; index < quantity; index += 1) {
        const code = generateInviteCode(selectedPlan);
        await setDoc(doc(db, "inviteCodes", code), {
          code,
          plan: selectedPlan,
          status: "active",
          used: false,
          usedBy: null,
          usedByEmail: null,
          source: "admin",
          createdBy: currentUser.email,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
        createdCodes.push(code);
      }
      setAdminNotice(`Created ${createdCodes.length} ${getPlanLabel(selectedPlan)} code${createdCodes.length === 1 ? "" : "s"}.`);
      await loadAdminData();
    } catch (error) {
      setAdminError("Could not create invite codes.");
    } finally {
      setAdminBusy(false);
    }
  };

  const updateUserAccess = async (userId, nextPlan, nextStatus, successMessage = "User access updated.") => {
    setAdminBusy(true);
    setAdminError("");
    setAdminNotice("");

    try {
      await updateDoc(doc(db, "users", userId), {
        access: {
          plan: nextPlan,
          planStatus: nextStatus,
          source: "admin",
          inviteCode: "",
          updatedAt: serverTimestamp(),
        },
        updatedAt: serverTimestamp(),
      });
      setAdminNotice(successMessage);
      await loadAdminData();
    } catch (error) {
      setAdminError("Could not update that user.");
    } finally {
      setAdminBusy(false);
    }
  };

  const deleteUnusedCode = async (code) => {
    setAdminBusy(true);
    setAdminError("");
    setAdminNotice("");

    try {
      await deleteDoc(doc(db, "inviteCodes", code));
      setAdminNotice(`Deleted ${code}.`);
      await loadAdminData();
    } catch (error) {
      setAdminError("Could not delete that code.");
    } finally {
      setAdminBusy(false);
    }
  };

  const requestUserPlanChange = (user, nextPlan) => {
    if (!user?.id) return;

    const userAccess = normalizeAccess(user.access);
    const targetEmail = user.email || user.id;

    if (userAccess.plan === nextPlan && userAccess.planStatus === "active") {
      setAdminError("");
      setAdminNotice(`${targetEmail} already has active ${getPlanLabel(nextPlan)} access.`);
      return;
    }

    const confirmed = window.confirm(
      `Change ${targetEmail} to ${getPlanLabel(nextPlan)}?\n\nThis immediately updates the FLOW plan this user can access.`
    );

    if (!confirmed) return;

    updateUserAccess(user.id, nextPlan, "active", `${targetEmail} is now on ${getPlanLabel(nextPlan)}.`);
  };

  const suspendUserAccess = (user) => {
    if (!user?.id) return;

    const userAccess = normalizeAccess(user.access);
    const targetEmail = user.email || user.id;
    const nextPlan = userAccess.plan || "lite";
    const confirmed = window.confirm(
      `Suspend access for ${targetEmail}?\n\nThey will not be able to use FLOW until you restore access or assign a plan again.`
    );

    if (!confirmed) return;

    updateUserAccess(user.id, nextPlan, "suspended", `${targetEmail} has been suspended.`);
  };

  const restoreUserAccess = (user) => {
    if (!user?.id) return;

    const userAccess = normalizeAccess(user.access);
    const targetEmail = user.email || user.id;
    const nextPlan = userAccess.plan || "lite";
    const confirmed = window.confirm(
      `Restore ${getPlanLabel(nextPlan)} access for ${targetEmail}?\n\nThis makes their FLOW access active again.`
    );

    if (!confirmed) return;

    updateUserAccess(user.id, nextPlan, "active", `${targetEmail} has been restored to ${getPlanLabel(nextPlan)}.`);
  };

  const requestDeleteUnusedCode = (code) => {
    const targetCode = code.code || code.id;
    const confirmed = window.confirm(
      `Delete unused invite code ${targetCode}?\n\nThis cannot be undone. Only delete codes you have not sent to a buyer.`
    );

    if (!confirmed) return;

    deleteUnusedCode(code.id);
  };

  const deleteUserRecord = async (user) => {
    if (!user?.id) return;

    if (user.id === currentUser.uid) {
      setAdminError("You cannot delete the admin record you are currently signed in with.");
      setAdminNotice("");
      return;
    }

    const targetEmail = user.email || user.id;
    const confirmed = window.confirm(
      `Delete the FLOW app record for ${targetEmail}?\n\nThis removes their saved FLOW data and access record. It does not delete their Firebase Authentication login yet.\n\nOnly continue for spam, test accounts, or records you intentionally want removed.`
    );

    if (!confirmed) return;

    setAdminBusy(true);
    setAdminError("");
    setAdminNotice("");

    try {
      await deleteDoc(doc(db, "users", user.id));
      setAdminNotice(`Deleted app record for ${targetEmail}. If you need to reuse the same email for signup, also delete the matching user in Firebase Authentication.`);
      await loadAdminData();
    } catch (error) {
      setAdminError("Could not delete that user record.");
    } finally {
      setAdminBusy(false);
    }
  };

  return (
    <div className="min-h-screen w-full bg-[#0E0A12] text-[#D8D8D8] px-4 py-6 sm:px-6 lg:px-8">
      <div className="mx-auto flex w-full max-w-7xl flex-col gap-6">
        <div className="flex flex-col gap-4 rounded-3xl border border-[#2A0047] bg-black/80 p-6 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="flex flex-wrap items-center gap-3">
              <p className="text-xs uppercase tracking-[0.35em] text-[#9E4CFF]">FLOW Admin</p>
              <span className="rounded-full border border-[#5A4A1D] bg-[#211B0E] px-3 py-1 text-xs font-semibold uppercase tracking-[0.22em] text-[#FFE7A3]">
                Admin-only control panel
              </span>
            </div>
            <h1 className="mt-2 text-3xl font-bold text-white">Customer access manager</h1>
            <p className="mt-2 text-sm text-[#D8D8D8]">
              Signed in as <span className="font-semibold text-white">{currentUser.email}</span>. Use this page to create codes, switch plans, suspend users, and remove test or spam records.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <button type="button" onClick={loadAdminData} className="rounded-xl border border-[#2A0047] bg-black/60 px-4 py-2 text-sm font-semibold text-[#C9A6FF]">
              Refresh
            </button>
            <button type="button" onClick={onClose} className="rounded-xl border border-[#2A0047] bg-black/60 px-4 py-2 text-sm font-semibold text-[#C9A6FF]">
              Open App
            </button>
            <button type="button" onClick={onLogout} className="rounded-xl border border-[#2A0047] bg-black/60 px-4 py-2 text-sm font-semibold text-[#C9A6FF]">
              Log out
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
          {[
            { label: "Total users", value: users.length },
            { label: "Active users", value: activeUsers },
            { label: "Suspended", value: suspendedUsers },
            { label: "Unused codes", value: unusedCodes },
            { label: "Used codes", value: usedCodes },
          ].map((item) => (
            <div key={item.label} className="rounded-2xl border border-[#2A0047] bg-black/70 p-4">
              <p className="text-xs uppercase tracking-[0.25em] text-[#9E4CFF]">{item.label}</p>
              <p className="mt-2 text-3xl font-bold text-white">{item.value}</p>
            </div>
          ))}
        </div>

        {adminError ? <div className="rounded-xl border border-[#5A1D55] bg-[#2A0A2E] px-4 py-3 text-sm text-[#FFD4F4]">{adminError}</div> : null}
        {adminNotice ? <div className="rounded-xl border border-[#2A5B44] bg-[#0E2119] px-4 py-3 text-sm text-[#CFFBE4]">{adminNotice}</div> : null}

        <SectionCard title="Search admin records" subtitle="Find a customer, Firebase user ID, invite code, Gumroad license key, plan, or status before making changes." dark>
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-[1fr_auto_auto] lg:items-end">
            <label className="flex flex-col gap-2">
              <span className="text-[#9E4CFF] uppercase tracking-widest text-xs">Search</span>
              <input
                value={adminSearch}
                onChange={(event) => setAdminSearch(event.target.value)}
                type="search"
                placeholder="Email, user ID, invite code, license key, plan, or status"
                className="bg-black/70 border border-[#2A0047] rounded-lg px-3 py-3 outline-none focus:border-[#9E4CFF]"
              />
            </label>
            <div className="rounded-xl border border-[#2A0047] bg-black/60 px-4 py-3 text-sm text-[#D8D8D8]">
              Showing <span className="font-semibold text-white">{filteredUsers.length}</span> users and{" "}
              <span className="font-semibold text-white">{filteredInviteCodes.length}</span> codes
            </div>
            <button
              type="button"
              onClick={() => setAdminSearch("")}
              disabled={!adminSearch}
              className="rounded-xl border border-[#2A0047] bg-black/60 px-4 py-3 text-sm font-semibold text-[#C9A6FF] disabled:opacity-40"
            >
              Clear search
            </button>
          </div>
        </SectionCard>

        <SectionCard title="Generate invite codes" subtitle="Create manual one-time FLOW codes. Gumroad license keys are verified automatically when the Firebase Function is configured." dark>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-[1fr_180px_180px]">
            <label className="flex flex-col gap-2">
              <span className="text-[#9E4CFF] uppercase tracking-widest text-xs">Plan</span>
              <select value={selectedPlan} onChange={(event) => setSelectedPlan(event.target.value)} className="bg-black/70 border border-[#2A0047] rounded-lg px-3 py-3 outline-none focus:border-[#9E4CFF]">
                {FLOW_PLAN_OPTIONS.map((plan) => (
                  <option key={plan.id} value={plan.id}>{plan.label}</option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-2">
              <span className="text-[#9E4CFF] uppercase tracking-widest text-xs">Quantity</span>
              <input value={codeCount} onChange={(event) => setCodeCount(event.target.value)} type="number" min="1" max="100" className="bg-black/70 border border-[#2A0047] rounded-lg px-3 py-3 outline-none focus:border-[#9E4CFF]" />
            </label>
            <button type="button" onClick={createCodes} disabled={adminBusy} className="self-end rounded-xl bg-[#9E4CFF] px-4 py-3 font-semibold text-black shadow-[0_0_16px_#9E4CFF] disabled:opacity-50">
              Generate
            </button>
          </div>
        </SectionCard>

        <SectionCard title="Invite codes" subtitle="Unused manual codes can be copied into buyer emails if Gumroad license delivery is unavailable." dark>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] text-left text-sm">
              <thead className="text-xs uppercase tracking-widest text-[#9E4CFF]">
                <tr>
                  <th className="py-2">Code</th>
                  <th className="py-2">Plan</th>
                  <th className="py-2">Status</th>
                  <th className="py-2">Used by</th>
                  <th className="py-2">Created</th>
                  <th className="py-2 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredInviteCodes.map((code) => {
                  const status = code.used ? "used" : code.status || "active";

                  return (
                    <tr key={code.id} className="border-t border-[#2A0047]">
                      <td className="py-3 font-mono text-[#CFFBE4]">{code.code || code.id}</td>
                      <td className="py-3">
                        <span className="inline-flex rounded-full border border-[#2A0047] bg-black/70 px-3 py-1 text-xs font-semibold text-[#C9A6FF]">
                          {getPlanLabel(code.plan)}
                        </span>
                      </td>
                      <td className="py-3">
                        <span className={`inline-flex rounded-full border px-3 py-1 text-xs font-semibold ${getStatusPillClass(status)}`}>
                          {getStatusLabel(status)}
                        </span>
                      </td>
                      <td className="py-3">{code.usedByEmail || "-"}</td>
                      <td className="py-3">{formatAdminTimestamp(code.createdAt)}</td>
                      <td className="py-3 text-right">
                        {!code.used ? (
                          <button type="button" onClick={() => requestDeleteUnusedCode(code)} className="rounded-lg border border-[#5A1D55] bg-[#2A0A2E] px-3 py-2 text-xs font-semibold text-[#FFD4F4]">
                            Delete unused
                          </button>
                        ) : (
                          <span className="text-xs text-[#D8D8D8]">Locked after use</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {filteredInviteCodes.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="py-4 text-[#D8D8D8]">
                      {normalizedAdminSearch ? "No invite codes match this search." : "No invite codes yet."}
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </SectionCard>

        <SectionCard title="User access" subtitle="Upgrade, downgrade, suspend, or delete saved FLOW app records." dark>
          <p className="mb-4 rounded-xl border border-[#5A4A1D] bg-[#211B0E] px-4 py-3 text-xs text-[#FFE7A3]">
            Delete removes the FLOW app data record only. To reuse the exact same email for signup, delete that email from Firebase Authentication too, unless we add a backend Admin SDK delete function later.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[960px] text-left text-sm">
              <thead className="text-xs uppercase tracking-widest text-[#9E4CFF]">
                <tr>
                  <th className="py-2">Email</th>
                  <th className="py-2">Name</th>
                  <th className="py-2">Plan</th>
                  <th className="py-2">Status</th>
                  <th className="py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user) => {
                  const userAccess = normalizeAccess(user.access);
                  const status = userAccess.planStatus || "pending";
                  const isCurrentAdmin = user.id === currentUser.uid;

                  return (
                    <tr key={user.id} className="border-t border-[#2A0047]">
                      <td className="py-3">{user.email || "-"}</td>
                      <td className="py-3">{user.profile?.fullName || user.profile?.preferredName || "-"}</td>
                      <td className="py-3">
                        <span className="inline-flex rounded-full border border-[#2A0047] bg-black/70 px-3 py-1 text-xs font-semibold text-[#C9A6FF]">
                          {getPlanLabel(userAccess.plan)}
                        </span>
                      </td>
                      <td className="py-3">
                        <span className={`inline-flex rounded-full border px-3 py-1 text-xs font-semibold ${getStatusPillClass(status)}`}>
                          {getStatusLabel(status)}
                        </span>
                      </td>
                      <td className="py-3">
                        <div className="flex flex-wrap justify-end gap-2">
                          {FLOW_PLAN_OPTIONS.map((plan) => {
                            const isCurrentPlan = userAccess.plan === plan.id && status === "active";

                            return (
                              <button
                                key={plan.id}
                                type="button"
                                onClick={() => requestUserPlanChange(user, plan.id)}
                                disabled={adminBusy || isCurrentPlan}
                                className={`rounded-lg border px-3 py-2 text-xs font-semibold disabled:cursor-not-allowed ${
                                  isCurrentPlan
                                    ? "border-[#2A5B44] bg-[#0E2119] text-[#CFFBE4]"
                                    : "border-[#2A0047] bg-black/60 text-[#C9A6FF]"
                                }`}
                              >
                                {isCurrentPlan ? `${plan.label.replace("FLOW ", "")} active` : plan.label.replace("FLOW ", "")}
                              </button>
                            );
                          })}
                          {status === "suspended" ? (
                            <button type="button" onClick={() => restoreUserAccess(user)} className="rounded-lg border border-[#2A5B44] bg-[#0E2119] px-3 py-2 text-xs font-semibold text-[#CFFBE4]">
                              Restore
                            </button>
                          ) : (
                            <button type="button" onClick={() => suspendUserAccess(user)} className="rounded-lg border border-[#5A1D55] bg-[#2A0A2E] px-3 py-2 text-xs font-semibold text-[#FFD4F4]">
                              Suspend
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => deleteUserRecord(user)}
                            disabled={adminBusy || isCurrentAdmin}
                            title={isCurrentAdmin ? "You cannot delete the admin record you are currently signed in with." : "Delete this user's FLOW app data record."}
                            className="rounded-lg border border-[#7A1E1E] bg-[#3A0909] px-3 py-2 text-xs font-semibold text-[#FFB4B4] disabled:opacity-40 disabled:cursor-not-allowed"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="py-4 text-[#D8D8D8]">
                      {normalizedAdminSearch ? "No users match this search." : "No users found."}
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </SectionCard>
      </div>
    </div>
  );
}

export default function App() {
  const [authMode, setAuthMode] = useState("login");
  const [authForm, setAuthForm] = useState(createInitialAuthForm());
  const [authError, setAuthError] = useState("");
  const [authNotice, setAuthNotice] = useState("");
  const [authBusy, setAuthBusy] = useState(false);
  const [authUser, setAuthUser] = useState(null);
  const [userProfile, setUserProfile] = useState(normalizeUserProfile());
  const [userAccess, setUserAccess] = useState(normalizeAccess());
  const [isAdminUser, setIsAdminUser] = useState(false);
  const [inviteCodeInput, setInviteCodeInput] = useState("");
  const [showAdminConsole, setShowAdminConsole] = useState(() => typeof window !== "undefined" && new URLSearchParams(window.location.search).get("admin") === "1");
  const [authLoading, setAuthLoading] = useState(true);
  const [accountSetupFinalizing, setAccountSetupFinalizing] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);
  const [saveStatus, setSaveStatus] = useState("idle");
  const [initialBudgetState, setInitialBudgetState] = useState(createInitialBudgetState());
  const [dashboardKey, setDashboardKey] = useState(0);
  const [showPublicDemo, setShowPublicDemo] = useState(() => isDemoQueryEnabled());
  const [showIntroSplash, setShowIntroSplash] = useState(() => !isDemoQueryEnabled());
  const [productMode, setProductMode] = useState(() => getProductModeFromQuery());
  const [demoBudgetState] = useState(() => createDemoBudgetState());
  const [demoLiteBudgetState] = useState(() => createDemoBudgetState("lite"));
  const [demoProfile] = useState(() =>
    normalizeUserProfile({
      fullName: "Demo Mode",
      preferredName: "Demo Mode",
      preferences: {
        themeMode: "theme-dark-plum",
        accentColor: "plum",
      },
    })
  );

  const hasHydratedRef = useRef(false);
  const accountSetupInProgressRef = useRef(false);
  const redeemedAccessPendingRef = useRef(false);
  const latestUserAccessRef = useRef(normalizeAccess());
  const latestBudgetStateRef = useRef(createInitialBudgetState());
  const lastSavedSnapshotRef = useRef("");
  const saveTimeoutRef = useRef(null);

  useEffect(() => {
    if (!showIntroSplash) {
      return undefined;
    }

    const introTimer = setTimeout(() => {
      setShowIntroSplash(false);
    }, 2400);

    return () => clearTimeout(introTimer);
  }, [showIntroSplash]);

  useEffect(() => {
    latestUserAccessRef.current = userAccess;
  }, [userAccess]);

  const flushPendingBudgetSave = async (userOverride = auth.currentUser) => {
    const targetUser = userOverride || auth.currentUser;
    if (!targetUser || !hasHydratedRef.current) {
      return true;
    }

    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }

    const nextSnapshot = JSON.stringify(latestBudgetStateRef.current);
    if (nextSnapshot === lastSavedSnapshotRef.current) {
      if (saveStatus === "saving") {
        setSaveStatus("saved");
      }
      return true;
    }

    try {
      setSaveStatus("saving");
      await setDoc(
        doc(db, "users", targetUser.uid),
        {
          email: targetUser.email,
          budgetState: latestBudgetStateRef.current,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
      lastSavedSnapshotRef.current = nextSnapshot;
      setSaveStatus("saved");
      return true;
    } catch (error) {
      setSaveStatus("error");
      return false;
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setAuthLoading(true);
      setAuthError("");
      setAuthNotice("");
      setSaveStatus("idle");
      hasHydratedRef.current = false;

      if (!user) {
        accountSetupInProgressRef.current = false;
        setAccountSetupFinalizing(false);
        if (saveTimeoutRef.current) {
          clearTimeout(saveTimeoutRef.current);
          saveTimeoutRef.current = null;
        }
        setAuthUser(null);
        setUserProfile(normalizeUserProfile());
        setUserAccess(normalizeAccess());
        setIsAdminUser(false);
        redeemedAccessPendingRef.current = false;
        latestUserAccessRef.current = normalizeAccess();
        setInviteCodeInput("");
        setShowAdminConsole(false);
        setInitialBudgetState(createInitialBudgetState());
        latestBudgetStateRef.current = createInitialBudgetState();
        lastSavedSnapshotRef.current = "";
        setDashboardKey((value) => value + 1);
        setAuthLoading(false);
        return;
      }

      setShowPublicDemo(false);
      setDemoQueryEnabled(false);
      setAuthUser({ ...user });
      setDataLoading(true);

      try {
        const tokenResult = await user.getIdTokenResult();
        const nextIsAdminUser = tokenResult.claims?.admin === true && user.emailVerified === true;
        const snapshot = await getDoc(doc(db, "users", user.uid));
        const snapshotData = snapshot.exists() ? snapshot.data() : {};
        const nextState = snapshotData?.budgetState ? mergeBudgetState(snapshotData.budgetState) : createInitialBudgetState();
        const nextProfile = normalizeUserProfile(snapshotData?.profile);
        const nextAccess = normalizeAccess(snapshotData?.access);
        const localAccess = normalizeAccess(latestUserAccessRef.current);
        const shouldPreserveRedeemedAccess =
          redeemedAccessPendingRef.current && isAccessActive(localAccess) && !isAccessActive(nextAccess);
        const effectiveAccess = shouldPreserveRedeemedAccess ? localAccess : nextAccess;
        latestBudgetStateRef.current = nextState;
        lastSavedSnapshotRef.current = JSON.stringify(nextState);
        setInitialBudgetState(nextState);
        setUserProfile(nextProfile);
        setUserAccess(effectiveAccess);
        setIsAdminUser(nextIsAdminUser);
        latestUserAccessRef.current = effectiveAccess;
        if (!shouldPreserveRedeemedAccess) {
          redeemedAccessPendingRef.current = false;
        }
        if (nextIsAdminUser && typeof window !== "undefined" && new URLSearchParams(window.location.search).get("admin") === "1") {
          setShowAdminConsole(true);
        } else {
          setShowAdminConsole(false);
        }

        if (snapshotData?.email !== user.email) {
          await setDoc(
            doc(db, "users", user.uid),
            {
              email: user.email,
              updatedAt: serverTimestamp(),
            },
            { merge: true }
          );
        }

        setDashboardKey((value) => value + 1);
      } catch (error) {
        const isAccountSetupInProgress = accountSetupInProgressRef.current;
        const currentLocalAccess = latestUserAccessRef.current;
        const hasActiveLocalAccess = isAccessActive(currentLocalAccess);
        if (!isAccountSetupInProgress && !hasActiveLocalAccess) {
          setAuthError("We could not load your saved budget right now.");
        }
        const fallbackState = createInitialBudgetState();
        latestBudgetStateRef.current = fallbackState;
        lastSavedSnapshotRef.current = JSON.stringify(fallbackState);
        setInitialBudgetState(fallbackState);
        setUserProfile(normalizeUserProfile());
        setIsAdminUser(false);
        setUserAccess((currentAccess) => {
          if (isAccountSetupInProgress && isAccessActive(currentAccess)) {
            latestUserAccessRef.current = currentAccess;
            return currentAccess;
          }

          if (hasActiveLocalAccess) {
            const preservedAccess = normalizeAccess(currentLocalAccess);
            latestUserAccessRef.current = preservedAccess;
            return preservedAccess;
          }

          const fallbackAccess = normalizeAccess({});
          latestUserAccessRef.current = fallbackAccess;
          return fallbackAccess;
        });
        setShowAdminConsole(false);
        setDashboardKey((value) => value + 1);
      } finally {
        hasHydratedRef.current = true;
        setDataLoading(false);
        setAuthLoading(false);
        if (!accountSetupInProgressRef.current) {
          setAccountSetupFinalizing(false);
        }
      }
    });

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = null;
      }
      if (typeof unsubscribe === "function") {
        unsubscribe();
      }
    };
  }, []);

  const refreshAccountStatus = async () => {
    if (!auth.currentUser) {
      return { ok: false, message: "You need to be logged in to refresh account status." };
    }

    try {
      await reload(auth.currentUser);
      const refreshedUser = auth.currentUser;
      if (!refreshedUser) {
        return { ok: false, message: "We could not refresh your account right now." };
      }

      await refreshedUser.getIdToken(true);
      const tokenResult = await refreshedUser.getIdTokenResult();
      const nextIsAdminUser = tokenResult.claims?.admin === true && refreshedUser.emailVerified === true;

      setAuthUser({ ...refreshedUser });
      setIsAdminUser(nextIsAdminUser);
      if (!nextIsAdminUser) {
        setShowAdminConsole(false);
      }
      await setDoc(
        doc(db, "users", refreshedUser.uid),
        {
          email: refreshedUser.email,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      return {
        ok: true,
        message: refreshedUser.emailVerified
          ? "Your account status is up to date and your email is verified."
          : "Your account status is refreshed. If you just clicked a verification link, try again in a moment.",
      };
    } catch (error) {
      return { ok: false, message: "We could not refresh your account status right now." };
    }
  };

  const sendResetLink = async (emailAddress) => {
    if (!emailAddress) {
      return { ok: false, message: "Enter your email first so we know where to send the reset link." };
    }

    try {
      await sendPasswordResetEmail(auth, emailAddress);
      return {
        ok: true,
        message: "If an account exists for that email, a password reset link has been sent. Check your inbox and spam folder.",
      };
    } catch (error) {
      const code = error?.code || "";
      if (code.includes("invalid-email")) {
        return { ok: false, message: "Please enter a valid email address." };
      }
      if (code.includes("user-not-found")) {
        return {
          ok: true,
          message: "If an account exists for that email, a password reset link has been sent. Check your inbox and spam folder.",
        };
      }
      return { ok: false, message: "We could not send a reset email right now. Please try again." };
    }
  };

  const handleSendVerification = async () => {
    if (!auth.currentUser) {
      return { ok: false, message: "You need to be logged in first." };
    }

    try {
      await sendEmailVerification(auth.currentUser);
      return {
        ok: true,
        message: `Verification email sent to ${auth.currentUser.email}. After you click it, come back and refresh account status.`,
      };
    } catch (error) {
      return { ok: false, message: "We could not send a verification email right now." };
    }
  };

  const handleRequestEmailChange = async (nextEmail) => {
    if (!auth.currentUser) {
      return { ok: false, message: "You need to be logged in to change your email." };
    }

    if (!nextEmail) {
      return { ok: false, message: "Enter the new email address you want to use." };
    }

    if (nextEmail === auth.currentUser.email) {
      return { ok: false, message: "That is already your current email address." };
    }

    try {
      await verifyBeforeUpdateEmail(auth.currentUser, nextEmail);
      return {
        ok: true,
        message: `We sent a verification link to ${nextEmail}. After you confirm it, come back and refresh account status.`,
      };
    } catch (error) {
      const code = error?.code || "";
      if (code.includes("requires-recent-login")) {
        return { ok: false, message: "For security, log out and log back in before changing your email." };
      }
      if (code.includes("email-already-in-use")) {
        return { ok: false, message: "That email is already being used by another account." };
      }
      if (code.includes("invalid-email")) {
        return { ok: false, message: "Please enter a valid email address." };
      }
      return { ok: false, message: "We could not start your email change right now." };
    }
  };

  const handleBudgetStateChange = (nextState) => {
    latestBudgetStateRef.current = nextState;

    if (!authUser || !hasHydratedRef.current) {
      return;
    }

    const nextSnapshot = JSON.stringify(nextState);
    if (nextSnapshot === lastSavedSnapshotRef.current) {
      if (saveStatus === "saving") {
        setSaveStatus("saved");
      }
      return;
    }

    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    setSaveStatus("saving");
    saveTimeoutRef.current = setTimeout(() => {
      flushPendingBudgetSave(auth.currentUser || authUser);
    }, 700);
  };

  const handleProfileUpdate = async (nextProfile) => {
    if (!authUser) {
      return { ok: false, message: "You need to be logged in to update your settings." };
    }

    const normalizedProfile = normalizeUserProfile(nextProfile);

    try {
      await setDoc(
        doc(db, "users", authUser.uid),
        {
          email: authUser.email,
          profile: normalizedProfile,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
      setUserProfile(normalizedProfile);
      return { ok: true, message: "Your profile and theme settings were saved." };
    } catch (error) {
      return { ok: false, message: "We could not save your settings right now." };
    }
  };

  const handleForgotPassword = async () => {
    setAuthError("");
    setAuthNotice("");
    const result = await sendResetLink(authForm.email.trim());
    if (result.ok) {
      setAuthNotice(result.message);
    } else {
      setAuthError(result.message);
    }
  };

  const handleAuthSubmit = async (event) => {
    event.preventDefault();
    setAuthError("");
    setAuthNotice("");

    if (!authForm.email || !authForm.password) {
      setAuthError("Please enter your email and password.");
      return;
    }

    if (authMode === "signup") {
      if (!authForm.fullName.trim()) {
        setAuthError("Please enter your full name.");
        return;
      }

      if (!normalizeAccessCode(authForm.inviteCode)) {
        setAuthError("Please enter your FLOW access code.");
        return;
      }

      if (authForm.password.length < 6) {
        setAuthError("Passwords must be at least 6 characters.");
        return;
      }
      if (authForm.password !== authForm.confirmPassword) {
        setAuthError("Your passwords do not match yet.");
        return;
      }
    }

    setAuthBusy(true);
    let createdUserForRollback = null;

    try {
      if (authMode === "login") {
        await signInWithEmailAndPassword(auth, authForm.email, authForm.password);
      } else {
        accountSetupInProgressRef.current = true;
        setAccountSetupFinalizing(true);
        const profile = normalizeUserProfile({
          fullName: authForm.fullName.trim(),
          preferredName: authForm.preferredName.trim(),
          dateOfBirth: authForm.dateOfBirth,
        });
        const credential = await createUserWithEmailAndPassword(auth, authForm.email, authForm.password);
        createdUserForRollback = credential.user;
        await credential.user.getIdToken(true);
        const redeemedAccess = await redeemAccessCode({
          code: authForm.inviteCode,
          user: credential.user,
          profile,
          budgetState: createInitialBudgetState(),
          createUserRecord: true,
        });
        createdUserForRollback = null;
        setUserProfile(profile);
        redeemedAccessPendingRef.current = true;
        setUserAccess(redeemedAccess);
        latestUserAccessRef.current = redeemedAccess;
        setAuthError("");
        if (auth.currentUser) {
          await sendEmailVerification(auth.currentUser);
        }
        const grantedMessage = getPlanAccessGrantedMessage(redeemedAccess.plan);
        if (typeof window !== "undefined") {
          window.alert(grantedMessage);
        }
        setAuthNotice(`Account created for ${credential.user.email}. ${grantedMessage} We sent a verification email so you can confirm your address when you're ready.`);
        accountSetupInProgressRef.current = false;
        setAccountSetupFinalizing(false);
      }
      setAuthForm(createInitialAuthForm());
    } catch (error) {
      accountSetupInProgressRef.current = false;
      setAccountSetupFinalizing(false);
      const code = error?.code || "";
      if (createdUserForRollback && authMode === "signup") {
        try {
          await deleteUser(createdUserForRollback);
        } catch (rollbackError) {
          // If rollback fails, the user can still log in and redeem a valid code from the access screen.
        }
      }

      if (error?.message?.includes("-code") || error?.message?.includes("invalid-plan") || error?.message?.includes("gumroad") || error?.code?.startsWith?.("functions/")) {
        setAuthError(getInviteCodeErrorMessage(error));
      } else if (code.includes("email-already-in-use")) {
        setAuthError("That email already has an account. Try logging in instead.");
      } else if (code.includes("invalid-credential") || code.includes("wrong-password") || code.includes("user-not-found")) {
        setAuthError("That email or password did not match an account.");
      } else if (code.includes("invalid-email")) {
        setAuthError("Please enter a valid email address.");
      } else {
        setAuthError("Something went wrong connecting your account. Please try again.");
      }
    } finally {
      setAuthBusy(false);
    }
  };

  const handleRedeemExistingAccountCode = async (event) => {
    event.preventDefault();
    setAuthError("");
    setAuthNotice("");

    if (!auth.currentUser || !authUser) {
      setAuthError("Log in before redeeming a FLOW invite code.");
      return;
    }

    setAuthBusy(true);
    accountSetupInProgressRef.current = true;
    setAccountSetupFinalizing(true);

    try {
      const redeemedAccess = await redeemAccessCode({
        code: inviteCodeInput,
        user: auth.currentUser,
        profile: userProfile,
        budgetState: latestBudgetStateRef.current,
        createUserRecord: false,
      });
      redeemedAccessPendingRef.current = true;
      setUserAccess(redeemedAccess);
      latestUserAccessRef.current = redeemedAccess;
      setInviteCodeInput("");
      const grantedMessage = getPlanAccessGrantedMessage(redeemedAccess.plan);
      if (typeof window !== "undefined") {
        window.alert(grantedMessage);
      }
      setAuthNotice(grantedMessage);
      setProductMode(getDashboardModeForAccess(redeemedAccess));
      setProductModeQuery(getDashboardModeForAccess(redeemedAccess));
      setDashboardKey((value) => value + 1);
    } catch (error) {
      setAuthError(getInviteCodeErrorMessage(error));
    } finally {
      accountSetupInProgressRef.current = false;
      setAccountSetupFinalizing(false);
      setAuthBusy(false);
    }
  };

  const handleLogout = async () => {
    try {
      const saveSucceeded = await flushPendingBudgetSave(auth.currentUser || authUser);
      if (!saveSucceeded) {
        setAuthError("We could not finish saving your latest budget changes before logging out. Please try again.");
        return;
      }
      setAuthNotice("");
      await signOut(auth);
    } catch (error) {
      setAuthError("We could not log you out right now.");
    }
  };

  const openPublicDemo = () => {
    setDemoQueryEnabled(true);
    setProductMode("lite");
    setProductModeQuery("lite");
    setShowPublicDemo(true);
    setAuthError("");
    setAuthNotice("");
  };

  const openAuthExperience = () => {
    setDemoQueryEnabled(false);
    setProductMode("full");
    setProductModeQuery("full");
    setShowPublicDemo(false);
    setAuthError("");
    setAuthNotice("");
  };

  const openAdminConsole = () => {
    if (!isAdminUser) {
      setAuthError("Admin access is not enabled for this account.");
      return;
    }
    setShowAdminConsole(true);
    if (typeof window !== "undefined") {
      const url = new URL(window.location.href);
      url.searchParams.set("admin", "1");
      window.history.replaceState({}, "", url.toString());
    }
  };

  const closeAdminConsole = () => {
    setShowAdminConsole(false);
    if (typeof window !== "undefined") {
      const url = new URL(window.location.href);
      url.searchParams.delete("admin");
      window.history.replaceState({}, "", url.toString());
    }
  };

  if (showIntroSplash || authLoading || dataLoading || accountSetupFinalizing) {
    const loadingLabel = accountSetupFinalizing
      ? "Activating your FLOW access..."
      : dataLoading
        ? "Loading your saved budget..."
        : showIntroSplash
          ? "Welcome to FLOW"
          : "Checking your account...";
    return <LoadingScreen label={loadingLabel} />;
  }

  if (!authUser) {
    if (showPublicDemo) {
      return (
        <BudgetDashboard
          key="public-demo"
          initialState={productMode === "lite" ? demoLiteBudgetState : demoBudgetState}
          accountEmail="Session-only demo"
          accountEmailVerified={false}
          userProfile={demoProfile}
          accountNotice="Public demo mode: nothing here saves to the cloud. Explore the app, then create an account when you want your numbers to stick."
          saveStatus="idle"
          onLogout={openAuthExperience}
          onBudgetStateChange={() => {}}
          onProfileUpdate={async () => ({ ok: false, message: "Demo mode does not save profile changes. Create an account to keep your settings." })}
          onSendPasswordReset={async () => ({ ok: false, message: "Password reset is only available for real accounts." })}
          onSendEmailVerification={async () => ({ ok: false, message: "Email verification is only available after you create an account." })}
          onRequestEmailChange={async () => ({ ok: false, message: "Email change is only available for real accounts." })}
          onRefreshAccountStatus={async () => ({ ok: false, message: "Demo mode does not have a real account status to refresh." })}
          isDemoMode
          productMode={productMode}
          onPrimaryAccountAction={openAuthExperience}
          primaryAccountActionLabel="Create Account"
        />
      );
    }

    return (
      <AuthScreen
        authMode={authMode}
        authForm={authForm}
        authError={authError}
        authNotice={authNotice}
        authBusy={authBusy}
        onChangeMode={(mode) => {
          setAuthMode(mode);
          setAuthError("");
          setAuthNotice("");
          setAuthForm((current) => ({
            ...createInitialAuthForm(),
            email: current.email,
          }));
        }}
        onChangeField={(field, value) => {
          setAuthForm((current) => ({ ...current, [field]: value }));
          if (authError) setAuthError("");
          if (authNotice) setAuthNotice("");
        }}
        onForgotPassword={handleForgotPassword}
        onSubmit={handleAuthSubmit}
        onTryDemo={openPublicDemo}
      />
    );
  }

  if (isAdminUser && showAdminConsole) {
    return <AdminConsole currentUser={authUser} onClose={closeAdminConsole} onLogout={handleLogout} />;
  }

  if (!isAccessActive(userAccess)) {
    return (
      <AccessRequiredScreen
        accountEmail={authUser.email}
        access={userAccess}
        authError={authError}
        authNotice={authNotice}
        authBusy={authBusy}
        inviteCode={inviteCodeInput}
        onInviteCodeChange={(value) => {
          setInviteCodeInput(value);
          if (authError) setAuthError("");
          if (authNotice) setAuthNotice("");
        }}
        onRedeemInviteCode={handleRedeemExistingAccountCode}
        onLogout={handleLogout}
        onOpenAdmin={openAdminConsole}
        isAdmin={isAdminUser}
      />
    );
  }

  return (
    <BudgetDashboard
      key={dashboardKey}
      initialState={initialBudgetState}
      accountEmail={authUser.email}
      accountEmailVerified={Boolean(authUser.emailVerified)}
      userProfile={userProfile}
      accountNotice={authNotice}
      saveStatus={saveStatus}
      onLogout={handleLogout}
      onBudgetStateChange={handleBudgetStateChange}
      onProfileUpdate={handleProfileUpdate}
      onSendPasswordReset={sendResetLink}
      onSendEmailVerification={handleSendVerification}
      onRequestEmailChange={handleRequestEmailChange}
      onRefreshAccountStatus={refreshAccountStatus}
      productMode={getDashboardModeForAccess(userAccess)}
      isAdmin={isAdminUser}
      onOpenAdminConsole={openAdminConsole}
    />
  );
}














